import React, { useState } from 'react';
import {
  X,
  Calendar,
  User,
  MapPin,
  Clock,
  Users,
  Link as LinkIcon,
  Edit,
  Save,
  FileText
} from 'lucide-react';
import Swal from 'sweetalert2';
import api from '../api';

const EvenementDetail = ({ evenement, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    titre: evenement.titre || '',
    description: evenement.description || '',
    lieu: evenement.lieu || '',
    date: evenement.date || '',
    date_fin: evenement.date_fin || '',
    heure_debut: evenement.heure_debut || '',
    heure_fin: evenement.heure_fin || '',
    lien_utile: evenement.lien_utile || ''
  });
  const [updating, setUpdating] = useState(false);

  if (!evenement) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.slice(0, 5);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
      const response = await api.patch(`/api/evenements/admin/evenements/${evenement.id}/`, {
        ...formData,
        auteur: evenement.auteur.id
      });

      Swal.fire({
        title: 'Modifié !',
        text: "L'événement a été modifié avec succès.",
        icon: 'success',
        confirmButtonColor: '#FF6B00',
        confirmButtonText: 'OK',
        timer: 2000,
        customClass: {
          popup: 'rounded-xl',
          confirmButton: 'rounded-lg px-6 py-2'
        }
      });

      if (onUpdate) {
        onUpdate(response.data);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      Swal.fire({
        title: 'Erreur !',
        text: "Erreur lors de la modification de l'événement.",
        icon: 'error',
        confirmButtonColor: '#FF6B00',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-xl',
          confirmButton: 'rounded-lg px-6 py-2'
        }
      });
    } finally {
      setUpdating(false);
    }
  };

  const isUpcoming = () => {
    const eventDate = new Date(evenement.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate >= today;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-[#FF6B00] to-[#437011]">
              <div className="flex items-center gap-3">
                <Calendar size={28} className="text-white" />
                <div>
                  <h2 className="text-2xl font-bold text-white">Détails de l'événement</h2>
                  <p className="text-white text-sm opacity-90">{evenement.titre}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setActiveTab('info')}
                className={`flex-1 px-6 py-3 font-medium transition-colors ${
                  activeTab === 'info'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Informations
              </button>
              <button
                onClick={() => setActiveTab('participants')}
                className={`flex-1 px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'participants'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Users size={18} />
                Participants ({evenement.participants_count || 0})
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'info' && (
                <div className="space-y-6">
                  {/* Image de couverture */}
                  {evenement.couverture && (
                    <div className="relative h-64 rounded-lg overflow-hidden">
                      <img
                        src={`http://192.168.1.65:8000${evenement.couverture}`}
                        alt={evenement.titre}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Statut */}
                  <div className={`rounded-lg p-4 ${
                    isUpcoming()
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className={isUpcoming() ? 'text-green-600' : 'text-gray-600'} />
                      <h3 className={`font-semibold ${isUpcoming() ? 'text-green-900' : 'text-gray-900'}`}>
                        Statut
                      </h3>
                    </div>
                    <p className={`ml-7 font-medium ${isUpcoming() ? 'text-green-700' : 'text-gray-700'}`}>
                      {isUpcoming() ? 'À venir' : 'Terminé'}
                    </p>
                  </div>

                  {/* Titre */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.titre}
                        onChange={(e) => handleInputChange('titre', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-lg font-semibold text-gray-900">{evenement.titre}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    {isEditing ? (
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent resize-none"
                      />
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {evenement.description || 'Aucune description'}
                      </p>
                    )}
                  </div>

                  {/* Lieu */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={18} className="text-blue-600" />
                      <label className="text-sm font-medium text-gray-700">Lieu</label>
                    </div>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.lieu}
                        onChange={(e) => handleInputChange('lieu', e.target.value)}
                        className="w-full ml-7 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900 ml-7">{evenement.lieu}</p>
                    )}
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-blue-900 mb-2">Date de début</label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                        />
                      ) : (
                        <p className="text-blue-700 font-medium">{formatDate(evenement.date)}</p>
                      )}
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-purple-900 mb-2">Date de fin</label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={formData.date_fin}
                          onChange={(e) => handleInputChange('date_fin', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                        />
                      ) : (
                        <p className="text-purple-700 font-medium">
                          {evenement.date_fin ? formatDate(evenement.date_fin) : 'Non spécifiée'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Heures */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock size={18} className="text-green-600" />
                        <label className="text-sm font-medium text-green-900">Heure de début</label>
                      </div>
                      {isEditing ? (
                        <input
                          type="time"
                          value={formData.heure_debut}
                          onChange={(e) => handleInputChange('heure_debut', e.target.value)}
                          className="w-full ml-7 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                        />
                      ) : (
                        <p className="text-green-700 font-medium ml-7">{formatTime(evenement.heure_debut)}</p>
                      )}
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock size={18} className="text-orange-600" />
                        <label className="text-sm font-medium text-orange-900">Heure de fin</label>
                      </div>
                      {isEditing ? (
                        <input
                          type="time"
                          value={formData.heure_fin}
                          onChange={(e) => handleInputChange('heure_fin', e.target.value)}
                          className="w-full ml-7 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                        />
                      ) : (
                        <p className="text-orange-700 font-medium ml-7">
                          {evenement.heure_fin ? formatTime(evenement.heure_fin) : 'Non spécifiée'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Lien utile */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <LinkIcon size={18} className="text-blue-600" />
                      <label className="text-sm font-medium text-gray-700">Lien utile</label>
                    </div>
                    {isEditing ? (
                      <input
                        type="url"
                        value={formData.lien_utile}
                        onChange={(e) => handleInputChange('lien_utile', e.target.value)}
                        placeholder="https://..."
                        className="w-full ml-7 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <div className="ml-7">
                        {evenement.lien_utile ? (
                          <a
                            href={evenement.lien_utile}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline break-all"
                          >
                            {evenement.lien_utile}
                          </a>
                        ) : (
                          <p className="text-gray-500">Aucun lien</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Auteur */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <User size={18} className="text-[#FF6B00]" />
                      <h3 className="font-semibold text-gray-900">Organisateur</h3>
                    </div>
                    <div className="flex items-center gap-3 ml-7">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                        {evenement.auteur?.first_name?.[0] || evenement.auteur?.username?.[0] || 'A'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {evenement.auteur?.first_name && evenement.auteur?.last_name
                            ? `${evenement.auteur.first_name} ${evenement.auteur.last_name}`
                            : evenement.auteur?.username || 'Organisateur'}
                        </p>
                        <p className="text-sm text-gray-600">{evenement.auteur?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Date de création */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={18} className="text-gray-600" />
                      <h3 className="font-semibold text-gray-900">Créé le</h3>
                    </div>
                    <p className="text-gray-700 ml-7">{formatDateTime(evenement.date_creation)}</p>
                  </div>

                  {/* Statistiques */}
                  <div className="bg-gradient-to-r from-[#FF6B00] to-[#437011] rounded-lg p-6 text-white">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Users size={20} />
                      Participants
                    </h3>
                    <p className="text-4xl font-bold">{evenement.participants_count || 0}</p>
                  </div>
                </div>
              )}

              {activeTab === 'participants' && (
                <div className="text-center py-12">
                  <Users size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Liste des participants à implémenter</p>
                </div>
              )}
            </div>

            {/* Footer - Actions */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        titre: evenement.titre || '',
                        description: evenement.description || '',
                        lieu: evenement.lieu || '',
                        date: evenement.date || '',
                        date_fin: evenement.date_fin || '',
                        heure_debut: evenement.heure_debut || '',
                        heure_fin: evenement.heure_fin || '',
                        lien_utile: evenement.lien_utile || ''
                      });
                    }}
                    className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updating}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={18} />
                    {updating ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Edit size={18} />
                  Modifier
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvenementDetail;
